import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { cn } from '../cn';

/**
 * Restrained status text (ADR-005). The default is plain text; solid navy is reserved for a
 * label that genuinely needs emphasis; success/error colours appear only as text and icons.
 * There are deliberately no outlined pills or tinted badges.
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-md border text-xs font-medium [&_svg]:size-3.5',
  {
    variants: {
      tone: {
        /** Genuine emphasis — solid navy. */
        solid: 'border-navy-900 bg-navy-900 px-2 py-0.5 font-semibold text-white',
        /** Plain-text status, no container. */
        plain: 'border-transparent text-muted-600',
        /** Success meaning — plain text and icon only. */
        success: 'border-transparent text-ok-700',
        /** Error meaning — plain text and icon only. */
        danger: 'border-transparent text-danger-700',
      },
    },
    defaultVariants: { tone: 'plain' },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}
